// 12 Showcase V3 → V4 on the project page (sections/ns-showcase-project.liquid; 6A re-run H2). The follow flow is a dialog that
// the card's Guess the launch price and Follow project buttons, the desktop side card and the phone bar open
// ([data-ns-follow-open] names the dialog). Guess lands on the price slider, Follow on the first question. Esc is the
// dialog's own cancel, a click on the backdrop closes it, Tab stays inside, and closing gives focus back to the opener. A link
// to the page's #follow (a related tile's or a reel's Follow) opens it. ns-showcase.js still posts the form.
// In place (gap inventory 2026-10-18 #18; snippets/ns-showcase-follow-dialog.liquid): on the Showcase collection and the home
// wall one dialog holds a hidden follow form per open project, and an opener with data-ns-follow-project shows that project's
// form. Its href still leads to the project page's flow, which is where a project without a form here goes.
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

// A control counts while nothing above it is hidden and no closed <details> folds it away (its summary stays).
const visible = node => {
  for (let at = node; at; at = at.parentElement) {
    if (at.hidden) return false;
    if (at.parentElement?.tagName === 'DETAILS' && !at.parentElement.open && at.tagName !== 'SUMMARY') return false;
  }
  return true;
};
const controls = root => [...root.querySelectorAll(FOCUSABLE)].filter(visible);
const outside = (rect, x, y) => x < rect.left || x > rect.right || y < rect.top || y > rect.bottom;

// Shows the named project's form and title in an in-place dialog; false, and nothing changes, when it holds no such form.
export function showFollowPanel(dialog, handle) {
  const panels = [...dialog.querySelectorAll('[data-ns-follow-panel]')];
  const panel = panels.find(node => node.dataset.nsFollowPanel === handle);
  if (!panel) return false;
  for (const node of panels) node.hidden = node !== panel;
  const title = dialog.querySelector('[data-ns-follow-title]');
  if (title && panel.dataset.title) title.textContent = panel.dataset.title;
  return true;
}

export function initFollowDialogs(doc, { view = doc.defaultView } = {}) {
  const openers = new WeakMap();
  const open = (dialog, opener, focus) => {
    if (!dialog.open) dialog.showModal();
    if (opener) openers.set(dialog, opener);
    const form = [...dialog.querySelectorAll('[data-ns-showcase-form]')].find(visible);
    const target = (focus === 'guess' && dialog.querySelector('[data-ns-guess]')) || (form && controls(form)[0]) || controls(dialog)[0];
    target?.focus();
  };
  for (const dialog of doc.querySelectorAll('[data-ns-follow-dialog]')) {
    dialog.addEventListener('close', () => {
      const opener = openers.get(dialog); openers.delete(dialog); opener?.focus();
      // A #follow link opened it: the hash goes, so the next Follow link fires hashchange again and a reload stays closed.
      if (view?.location?.hash === '#follow' && view.history?.replaceState) view.history.replaceState(view.history.state, '', `${view.location.pathname}${view.location.search}`);
    });
    dialog.addEventListener('keydown', event => {
      if (event.key !== 'Tab') return;
      const list = controls(dialog);
      if (!list.length) return;
      const at = list.indexOf(doc.activeElement);
      const next = event.shiftKey ? (at <= 0 ? list.length - 1 : null) : (at === -1 || at === list.length - 1 ? 0 : null);
      if (next === null) return;
      event.preventDefault();
      list[next].focus();
    });
    dialog.addEventListener('click', event => {
      if (event.target?.closest?.('[data-ns-follow-close]')) { dialog.close(); return; }
      if (event.target === dialog && outside(dialog.getBoundingClientRect(), event.clientX, event.clientY)) dialog.close();
    });
  }
  doc.addEventListener('click', event => {
    const opener = event.target?.closest?.('[data-ns-follow-open]');
    if (!opener) return;
    const dialog = doc.getElementById(opener.dataset.nsFollowOpen);
    if (!dialog) return;
    if (opener.dataset.nsFollowProject !== undefined && !showFollowPanel(dialog, opener.dataset.nsFollowProject)) return;
    event.preventDefault?.();
    open(dialog, opener, opener.dataset.nsFollowFocus);
  });
  // The page's own #follow links (the reels player's Follow) only change the hash, so the flow opens on that too.
  const linked = () => {
    if (view?.location?.hash !== '#follow') return;
    const dialog = doc.querySelector('[data-ns-follow-dialog]');
    // An in-place dialog needs a project to show; only the project page's own flow opens from the hash.
    if (dialog && !dialog.querySelector('[data-ns-follow-panel]')) open(dialog, null);
  };
  view?.addEventListener?.('hashchange', linked);
  linked();
}

if (typeof document !== 'undefined') {
  const start = () => initFollowDialogs(document);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
}
